import { Component, ElementRef, OnInit, Renderer2, ViewChild, ChangeDetectorRef, Input, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';
import flatpickr from 'flatpickr';
import { Timestamp } from 'firebase/firestore';
import { DateFormatterService } from 'src/app/services/date-formatter.service';
import { OrdersApiService, Birthday, Playground, Location, ReservedSlot, BirthdayData } from 'src/app/services/orders-api.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss']
})

export class OrdersComponent implements OnInit, OnDestroy {

  @Input() businessId!: string;

  @ViewChild('backdrop') backdrop!: ElementRef;
  @ViewChild('orderExpand') orderExpand!: ElementRef;
  @ViewChild('birthdayToast') birthdayToast!: ElementRef;
  @ViewChild('birthdayDelete') birthdayDelete!: ElementRef;
  @ViewChild('cancelBirthdayModal') cancelBirthdayModal!: ElementRef;
  @ViewChild('cancelMessage') cancelMessage!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('locationFilterDropdown') locationFilterDropdown!: ElementRef;
  @ViewChild('statusFilterDropdown') statusFilterDropdown!: ElementRef;

  today: string = '';
  selectedDate!: string;
  selectedDateForm!: string;
  selectedValue: string = '';
  addBirthdayForm!: FormGroup;
  birthdays: Array<any> = [];
  allLocations!: Array<any>;
  formLocations!: Array<any>;
  groupedData: any[] = [];
  deliveryDays!: number;
  selectedValueAdd: string = '';
  selectedDateAdd: string | null = null;
  selectedLocation: any;
  filteredRooms: any[] = [];
  filteredLocations: any[] = [];
  minDate: string = '';
  selectedRoom: any;
  reservedBirthdaysForm: Array<{ start: string; end: string }> = [];
  startTime: string = '';
  endTime: string = '';
  editMode!: boolean;
  birthdayCard !: any;
  isReserved: boolean = false;
  isReadOnly: boolean = false;
  cancellationReason!: string;
  funCityPlaygroundsIds: string[] = environment.funCityPlaygroundsIds;
  mpIds: string[] = [];
  managedPlaygrounds: any[] = [];
  selectedPlaygroundId: string = '';
  isFunCityPlayground: boolean = false;
  isGeneratingPaymentLink: boolean = false;
  cancelReason: string = '';
  // Location filter state
  allLocationsSelected: boolean = true;
  selectedLocationIds: string[] = [];
  tempSelectedLocationIds: string[] = [];
  locationSearchTerm: string = '';
  // Status filter state
  allStatusesSelected: boolean = true;
  selectedStatuses: string[] = [];
  tempSelectedStatuses: string[] = [];
  statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'personalized', label: 'Personalized' },
    { value: 'reserved', label: 'Reserved' },
    { value: 'generic', label: 'Generic' },
    { value: 'cancelled', label: 'Cancelled' }
  ];
  // Date and time picker implementation
  @ViewChild('startTimeInput') startTimeInput!: ElementRef;
  @ViewChild('endTimeInput') endTimeInput!: ElementRef;
  @ViewChild('dateInput') dateInput!: ElementRef;
  @ViewChild('dateInput2') dateInput2!: ElementRef;
  private flatpickrInstance!: flatpickr.Instance;
  private flatpickrInstance2!: flatpickr.Instance;
  filterMode: 'today' | 'range' | 'month' | 'range1' = 'today';
  selectedRange: { start: string; end: string } | null = null;
  selectedRangeText: string = '';
  // Add property to store previous selection for proper state management
  private previousFilterMode: 'today' | 'range' | 'month' | 'range1' = 'today';

  constructor(
    private fb: FormBuilder,
    public loadingSpinnerService: LoadingSpinnerService,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private dateFormatter: DateFormatterService,
    private ordersApi: OrdersApiService,
    private http: HttpClient
  ) {
    this.initForm();
    // Initialize status filter to All selected (select all status options)
    this.allStatusesSelected = true;
    this.tempSelectedStatuses = this.statusOptions
      .filter(s => s.value !== 'all')
      .map(s => s.value);
    this.selectedStatuses = [];
  }

  private initForm(): void {
    this.addBirthdayForm = this.fb.group({
      choice: ["", Validators.required],
      selectDateForm: ['', Validators.required],
      playground: [''], // UI-only field for selection logic
      location: ['', Validators.required],
      room: ['', Validators.required],
      name: ['', Validators.required],
      age: ['', [Validators.required, Validators.min(0)]],
      numberOfKids: ['', Validators.required],
      startTime: ['', Validators.required],
      endTime: ['', Validators.required],
      orderNote: [''],
      orderStatus: [''],
    });
  }

  // Alternatively, map controls for readability
  get formControls(): { [key: string]: AbstractControl } {
    return this.addBirthdayForm.controls;
  }

  ngAfterViewInit() {
    this.flatpickrInstance2 = flatpickr(this.dateInput2.nativeElement, {
      dateFormat: 'Y-m-d',
      defaultDate: new Date(),
      minDate: this.minDate,
      disableMobile: true,
    });

    // Initialize the main datepicker
    this.reinitTopDatepicker();

    flatpickr(this.startTimeInput.nativeElement, {
      enableTime: true,
      noCalendar: true,
      dateFormat: 'H:i',
      time_24hr: true,
      disableMobile: true,
    });

    flatpickr(this.endTimeInput.nativeElement, {
      enableTime: true,
      noCalendar: true,
      dateFormat: 'H:i',
      time_24hr: true,
      disableMobile: true,
    });
  }

  openCalendar() {
    if (this.filterMode === 'range' || this.filterMode === 'range1') {
      // Clear previous selection and reinitialize for fresh state
      this.clearDateInput();
      this.reinitTopDatepicker();
      if (this.flatpickrInstance) {
        this.flatpickrInstance.open();
      }
    }
  }

  openCalendar2() {
    if (this.flatpickrInstance2) {
      this.flatpickrInstance2.open();
    }
  }

  // Method to handle manual calendar opening for the main date input
  onDateInputClick() {
    if (this.filterMode === 'range' || this.filterMode === 'range1') {
      // Clear previous selection and reinitialize for fresh state
      this.clearDateInput();
      this.reinitTopDatepicker();
      if (this.flatpickrInstance) {
        this.flatpickrInstance.open();
      }
    }
  }

  onFilterModeChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    const newFilterMode = target.value as 'today' | 'range' | 'month' | 'range1';

    // Store previous selection before updating
    if (this.filterMode !== 'range') {
      this.previousFilterMode = this.filterMode;
    }

    this.filterMode = newFilterMode;
    // Trigger change detection to update the dropdown
    this.cdr.detectChanges();

    // Reset range state when switching
    if (this.filterMode === 'today') {
      this.selectedRange = null;
      this.selectedRangeText = '';
      // default to today and fetch
      this.selectedDate = this.today;
      this.getBirthdays(this.selectedDate);

    } else if (this.filterMode === 'month') {
      const now = new Date();
      // Start of current month
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      // End of current month (day 0 of next month = last day of this month)
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const startStr = this.dateFormatter.formatLocalDateForInput(startOfMonth);
      const endStr = this.dateFormatter.formatLocalDateForInput(endOfMonth);

      this.selectedRange = { start: startStr, end: endStr };
      this.selectedRangeText = `${startStr} to ${endStr}`;
      this.getBirthdaysRange(startStr, endStr);
    }
    else if (this.filterMode === 'range1') {
      // This is the trigger option to open the date picker
      // Don't change the actual filterMode yet, just open the calendar
      setTimeout(() => this.openCalendar());
      return; // Exit early, don't reinitialize or change filterMode
    } else if (this.filterMode === 'range') {
      // If there's already a selected range, keep it; otherwise open calendar
      if (this.selectedRange && this.selectedRangeText) {
        // Range already selected, just fetch data
        this.getBirthdaysRange(this.selectedRange.start, this.selectedRange.end);
      } else {
        // No range selected, open calendar for selection
        setTimeout(() => this.resetCustomRange());
      }
    }
    // Always reinitialize the datepicker when mode changes (except for range1)
    this.reinitTopDatepicker();
  }

  private reinitTopDatepicker() {
    // Always destroy the existing instance first
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
      this.flatpickrInstance = null as any;
    }

    const options: any = {
      dateFormat: 'Y-m-d',
      disableMobile: true,
    };

    if (this.filterMode === 'range' || this.filterMode === 'range1') {
      options.mode = 'range';

      options.onChange = (selectedDates: Date[], dateStr: string) => {
        // Only finalize when both dates are selected
        if (selectedDates.length === 2) {
          const [start, end] = selectedDates;
          const startStr = this.dateFormatter.formatLocalDateForInput(start);
          const endStr = this.dateFormatter.formatLocalDateForInput(end);

          this.selectedRange = { start: startStr, end: endStr };
          this.selectedRangeText = this.dateFormatter.formatRangeForDisplay(start, end);

          this.filterMode = 'range';
          this.cdr.detectChanges();

          this.getBirthdaysRange(startStr, endStr);

          if (this.flatpickrInstance) {
            this.flatpickrInstance.close();
          }
        }
      };

      options.onOpen = () => {
        // Clear any previous selection when opening the calendar
        this.clearDateInput();
      };

      options.onClose = (selectedDates: Date[]) => {
        if (this.filterMode === 'range1') {
          if (selectedDates.length < 2) {
            // User either picked nothing or only one date → revert
            this.revertToPreviousSelection();
            this.cdr.detectChanges();
          }
        }
      };

      if (this.dateInput?.nativeElement) {
        this.dateInput.nativeElement.value = '';
      }

      options.allowInput = false;
      options.clickOpens = true;
    } else {
      options.defaultDate = new Date();
    }

    this.flatpickrInstance = flatpickr(this.dateInput.nativeElement, options);
  }

  // Helper method to clear date input state
  private clearDateInput() {
    if (this.dateInput?.nativeElement) {
      this.dateInput.nativeElement.value = '';
    }
    this.selectedRange = null;
    this.selectedRangeText = '';
  }

  // Public method to reset custom range and reopen calendar
  resetCustomRange() {
    if (this.filterMode === 'range') {
      this.clearDateInput();
      this.reinitTopDatepicker();
      if (this.flatpickrInstance) {
        this.flatpickrInstance.open();
      }
    }
  }

  // Method to revert to previous selection (useful for cancel operations)
  revertToPreviousSelection() {
    this.filterMode = this.previousFilterMode;
    this.selectedRange = null;
    this.selectedRangeText = '';
    // Trigger change detection to update the dropdown
    this.cdr.detectChanges();
    // Fetch data based on the previous selection
    if (this.previousFilterMode === 'today') {
      this.selectedDate = this.today;
      this.getBirthdays(this.selectedDate);
    } else if (this.previousFilterMode === 'month') {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startStr = this.dateFormatter.formatLocalDateForInput(startOfMonth);
      const endStr = this.dateFormatter.formatLocalDateForInput(now);
      this.selectedRange = { start: startStr, end: endStr };
      this.selectedRangeText = `${startStr} to ${endStr}`;
      this.getBirthdaysRange(startStr, endStr);
    }
    this.reinitTopDatepicker();
  }

  // Method to go back to a specific selection
  goToSelection(selection: 'today' | 'month') {
    this.previousFilterMode = this.filterMode;
    this.filterMode = selection;
    // Trigger change detection to update the dropdown
    this.cdr.detectChanges();

    if (selection === 'today') {
      this.selectedRange = null;
      this.selectedRangeText = '';
      this.selectedDate = this.today;
      this.getBirthdays(this.selectedDate);
    } else if (selection === 'month') {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startStr = this.dateFormatter.formatLocalDateForInput(startOfMonth);
      const endStr = this.dateFormatter.formatLocalDateForInput(now);
      this.selectedRange = { start: startStr, end: endStr };
      this.selectedRangeText = `${startStr} to ${endStr}`;
      this.getBirthdaysRange(startStr, endStr);
    }
    this.reinitTopDatepicker();
  }

  selectOption(value: string) {
    this.selectedValue = value;
  }

  toggleBackdrop(show: boolean): void {
    if (show) {
      // Initialize form state for new birthday creation when opening
      if (!this.editMode) {
        this.resetFormState();
      }
      this.backdrop.nativeElement.style.display = 'block';
    } else {
      // Always reset form state when closing, regardless of edit mode
      this.resetFormState();
      this.backdrop.nativeElement.style.display = 'none';
    }
  }

  private resetFormState(): void {
    // Reset form state for new birthday creation or when closing
    this.selectedValue = '';
    this.addBirthdayForm.reset({
      orderNote: '',
    });
    this.reservedBirthdaysForm = [];
    this.editMode = false;
    this.isReserved = false;
    this.isReadOnly = false;
    this.cancellationReason = '';
    if (this.managedPlaygrounds.length !== 1) {
      this.selectedPlaygroundId = '';
    }
    this.selectedRoom = null;
    this.filteredRooms = [];
    // Reset form controls
    this.addBirthdayForm.patchValue({
      location: '',
      room: ''
    });
    // Ensure form is enabled for new birthday creation
    this.addBirthdayForm.enable({ emitEvent: false });
    // If there is only one location/room, auto-apply them so the form remains valid even if the selects are hidden
    if (this.formLocations && this.formLocations.length === 1) {
      const onlyLocation = this.formLocations[0];
      this.selectedLocation = onlyLocation.id;
      this.addBirthdayForm.patchValue({ location: this.selectedLocation });
      this.filteredRooms = onlyLocation.rooms || [];
      if (this.filteredRooms.length >= 1) {
        // Prefer first room if multiple, or the only room
        this.selectedRoom = this.filteredRooms[0].id;
        this.addBirthdayForm.patchValue({ room: this.selectedRoom });
      } else {
        this.selectedRoom = null;
      }
      // If date is already chosen, refresh reserved slots with these defaults
      this.getReservedBirthdaysForm();
    }
  }

  onDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (this.filterMode === 'today') {
      this.loadingSpinnerService.show();
      this.selectedDate = input.value;
      this.getBirthdays(this.selectedDate);
    } else {
      // Expecting format "YYYY-MM-DD to YYYY-MM-DD"
      const parts = input.value.split(' to ');
      if (parts.length === 2 && parts[0] && parts[1]) {
        // Only show spinner and fetch data if we have a complete range
        this.loadingSpinnerService.show();
        this.selectedRange = { start: parts[0], end: parts[1] };
        this.selectedRangeText = input.value;
        this.getBirthdaysRange(parts[0], parts[1]);
      } else {
        // Incomplete range selection; do nothing for now
      }
    }
  }

  getBirthdays(date: string) {
    this.ordersApi.getBirthdays(date).subscribe({
      next: (birthdays: Birthday[]) => {
        this.birthdays = birthdays;
        this.groupedData = [];

        if (this.birthdays.length > 0) {
          setTimeout(() => {
            this.groupData();
          }, 500);
        }
      },
      error: (error) => {
        console.error('Error fetching Birthdays:', error);
      }
    });
  }

  getBirthdaysRange(startDate: string, endDate: string) {
    this.ordersApi.getBirthdaysRange(startDate, endDate).subscribe({
      next: (birthdays: Birthday[]) => {
        this.birthdays = birthdays;
        this.groupedData = [];

        if (this.birthdays.length > 0) {
          setTimeout(() => {
            this.groupData();
          }, 500);
        }
      },
      error: (error) => {
        console.error('Error fetching Birthdays (range):', error);
      }
    });
  }


  getManagedPlaygrounds() {
    this.ordersApi.getManagedPlaygrounds().subscribe({
      next: (playgrounds: Playground[]) => {
        this.managedPlaygrounds = playgrounds;
        console.log(this.managedPlaygrounds);
        // Calculate magnets count after playgrounds are loaded
        this.calculateMagnetsCount();
        // Auto-select if only one playground available
        if (this.managedPlaygrounds.length === 1) {
          // Ensure the form control is populated and dependent data is loaded
          this.handlePlaygroundChange(this.managedPlaygrounds[0].uid);
        }
      },
      error: (error) => {
        console.error('Error fetching managed playgrounds:', error);
      }
    });
  }

  handlePlaygroundChange(playgroundId: string): void {
    const selectedPlayground = this.managedPlaygrounds.find(p => p.uid === playgroundId);
    if (selectedPlayground) {
      this.selectedPlaygroundId = selectedPlayground.uid;
      // Reset child selections when playground changes
      this.selectedLocation = null;
      this.selectedRoom = null;
      this.formLocations = [];
      this.filteredRooms = [];
      // Update form values (UI-only)
      this.addBirthdayForm.patchValue({
        playground: playgroundId
      });
      // Get locations for the selected playground
      this.getLocationsForPlayground(playgroundId);
      // Get delivery days for the selected playground
      this.getDeliveryDays();
    }
  }

  getLocationsForPlayground(playgroundId: string) {
    this.ordersApi.getLocationsForPlayground(playgroundId).subscribe({
      next: (locations: Location[]) => {
        this.formLocations = locations;
        // Auto-select if only one location available
        if (this.formLocations.length === 1) {
          this.handleLocationChange(this.formLocations[0].id);
        }
      },
      error: (error) => {
        console.error('Error getting locations for playground:', error);
        this.formLocations = [];
      }
    });
  }

  getLocations() {
    this.ordersApi.getLocations().subscribe({
      next: (locations: Location[]) => {
        this.allLocations = locations;
        // Initialize tempSelectedLocationIds with all locations if "All" is selected
        if (this.allLocationsSelected && this.allLocations.length > 0) {
          this.tempSelectedLocationIds = this.allLocations.map(l => l.id);
          // Also initialize selectedLocationIds to match the initial state
          this.selectedLocationIds = [...this.tempSelectedLocationIds];
        }
        // Recalculate magnets count now that locations are loaded (needed for filtering)
        this.calculateMagnetsCount();
        // Do not auto-select any form location here; this array is only for filtering/grouping
      },
      error: (error) => {
        console.error('Error fetching locations:', error);
      }
    });
  }

  getDeliveryDays() {
    if (!this.selectedPlaygroundId) {
      return;
    }

    this.ordersApi.getDeliveryDays(this.selectedPlaygroundId).subscribe({
      next: (deliveryDays: number) => {
        this.deliveryDays = deliveryDays;
      },
      error: (error) => {
        console.error('Error fetching delivery days:', error);
        this.deliveryDays = 0;
      }
    });
  }

  groupData() {
    // Group by date first, then by location within that date
    const dateMap = new Map<string, any[]>();
    for (const b of this.birthdays) {
      const dateObj = new Date(b.startTime);
      const dateKey = dateObj.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });

      if (!dateMap.has(dateKey)) dateMap.set(dateKey, []);
      dateMap.get(dateKey)!.push(b);
    }

    // Sort date keys descending
    const sortedDateKeys = Array.from(dateMap.keys()).sort((a, b) => {
      return new Date(b).getTime() - new Date(a).getTime();
    });

    this.groupedData = sortedDateKeys.map(dateKey => {
      const birthdaysForDate = dateMap.get(dateKey) || [];
      // Group by location for this date
      const locationMap = new Map<string, any[]>();
      birthdaysForDate.forEach(b => {
        const locId = b.locationId;
        if (!locationMap.has(locId)) locationMap.set(locId, []);
        locationMap.get(locId)!.push(b);
      });

      let locationsForDate = Array.from(locationMap.entries())
        .map(([locId, bs]) => {
          const loc = this.allLocations.find(l => l.id === locId);
          if (!loc) {
            // Skip birthdays from unknown locations (different account)
            return null;
          }
          const enriched = bs
            .map(birthday => ({
              ...birthday,
              roomName: loc.rooms.find((room: { id: any; }) => room.id === birthday.roomId)?.name || 'Unknown Room'
            }))
            .filter(birthday => this.matchesStatusFilter(birthday)) // Apply status filter
            .sort((a: any, b: any) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
          return {
            id: locId,
            name: loc.name,
            birthdays: enriched
          };
        })
        .filter((x: any) => !!x)
        .sort((a: any, b: any) => a.name.localeCompare(b.name));
      // Apply location selection filter
      if (!this.allLocationsSelected && this.selectedLocationIds.length > 0) {
        locationsForDate = locationsForDate.filter((l: any) => this.selectedLocationIds.includes(l.id));
      }

      return {
        date: dateKey,
        locations: locationsForDate
      };
    });
    this.loadingSpinnerService.hide();
  }

  handleLocationChange(locationId: string): void {
    const selectedLocation = this.formLocations.find(l => l.id === locationId);

    if (selectedLocation) {
      this.selectedLocation = selectedLocation;
      this.filteredRooms = selectedLocation.rooms || [];
      // Reset room selection when location changes
      this.selectedRoom = null;
      // Update form values
      this.addBirthdayForm.patchValue({
        location: locationId,
        room: ''
      });
      // Auto-select if only one room available
      if (this.filteredRooms.length === 1) {
        this.handleRoomChange(this.filteredRooms[0].id);
      }
    }
  }

  handleRoomChange(roomId: string): void {
    this.selectedRoom = this.filteredRooms.find(r => r.id === roomId);

    if (this.selectedRoom) {
      this.addBirthdayForm.patchValue({
        room: roomId
      });
      // Load reserved slots for the selected room
      this.getReservedBirthdaysForm();
    }
  }

  onPlaygroundChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const selectedPlaygroundId = target.value;
    this.handlePlaygroundChange(selectedPlaygroundId);
  }

  onLocationChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const selectedLocationId = target.value;
    this.handleLocationChange(selectedLocationId);
  }

  onRoomChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const selectedRoomId = target.value;
    this.handleRoomChange(selectedRoomId);
  }

  getReservedBirthdaysForm() {
    const selectedDate = this.addBirthdayForm.get('selectDateForm')?.value;
    const location = this.addBirthdayForm.get('location')?.value;
    const room = this.addBirthdayForm.get('room')?.value;

    if (!selectedDate || !location || !room) {
      return;
    }
    if (!this.selectedPlaygroundId) {
      return;
    }

    this.ordersApi.getReservedSlots(this.selectedPlaygroundId, location, room, selectedDate).subscribe({
      next: (reservedSlots: ReservedSlot[]) => {
        this.reservedBirthdaysForm = reservedSlots;
      },
      error: (error) => {
        console.error('Error fetching Reserved Birthdays:', error);
        this.reservedBirthdaysForm = [];
      }
    });
  }

  // Helper method to refresh data based on current filter mode
  private refreshData(): void {
    if (this.filterMode === 'today') {
      // this.getBirthdays(this.selectedDate);
      this.getBirthdaysRange(this.selectedDate, this.selectedDate);
    } else if (this.filterMode === 'month' || this.filterMode === 'range') {
      const startDate = this.selectedRange?.start || this.today;
      const endDate = this.selectedRange?.end || this.today;
      this.getBirthdaysRange(startDate, endDate);
    }
  }

  onStartTimeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.startTime = input.value;
    // Automatically set end time to start time + 2 hours
    const [startHours, startMinutes] = this.startTime.split(':').map(Number);
    const endDate = new Date();
    endDate.setHours(startHours + 2, startMinutes, 0); // Add hours locally
    const formattedEndTime = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`; // Local "HH:mm"
    // Update the start and end time controls in the form
    this.addBirthdayForm.patchValue({
      startTime: this.startTime,
      endTime: formattedEndTime
    });

    this.endTime = formattedEndTime; // Sync the endTime variable
  }

  onEndTimeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.endTime = input.value;
    // Update the end time control in the form
    this.addBirthdayForm.patchValue({
      endTime: this.endTime
    });
  }

  isTimeConflict(): boolean {
    const startTimeInMinutes = this.getMinutes(this.startTime);
    const endTimeInMinutes = this.getMinutes(this.endTime);

    return this.reservedBirthdaysForm.some(birthday => {
      const reservedStart = this.getMinutes(birthday.start);
      const reservedEnd = this.getMinutes(birthday.end);

      return (
        (startTimeInMinutes >= reservedStart && startTimeInMinutes < reservedEnd) || // Overlaps start
        (endTimeInMinutes > reservedStart && endTimeInMinutes <= reservedEnd) ||   // Overlaps end
        (startTimeInMinutes <= reservedStart && endTimeInMinutes >= reservedEnd)   // Contains reservation
      );
    });
  }

  private getMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }


  bookBirthday() {
    if (!this.selectedPlaygroundId) {
      this.showToast('Please select a playground first', 'error');
      return;
    }

    this.loadingSpinnerService.show();

    const startDateTime = this.dateFormatter.combineDateAndTime(this.addBirthdayForm.get('selectDateForm')?.value, this.editMode ? this.addBirthdayForm.get('startTime')?.value : this.startTime);
    const endDateTime = this.dateFormatter.combineDateAndTime(this.addBirthdayForm.get('selectDateForm')?.value, this.editMode ? this.addBirthdayForm.get('endTime')?.value : this.endTime);
    const orderStatus = this.orderStatus();


    if (this.editMode) {
      // Use service for updates
      const updateData: Partial<BirthdayData> = {
        birthdayName: this.addBirthdayForm.get("name")?.value.trim(),
        birthdayYears: this.addBirthdayForm.get("age")?.value,
        numberOfMagnets: this.addBirthdayForm.get("numberOfKids")?.value,
        note: this.addBirthdayForm.get("orderNote")?.value,
        startTime: Timestamp.fromDate(new Date(startDateTime)),
        endTime: Timestamp.fromDate(new Date(endDateTime)),
        locationId: this.addBirthdayForm.get("location")?.value,
        roomId: this.addBirthdayForm.get("room")?.value,
        services: [this.addBirthdayForm.get("choice")?.value === 'male' ? environment.serviceIds.MALE_ID : environment.serviceIds.FEMALE_ID],
      };
        this.ordersApi.updateBirthday(this.birthdayCard.id, updateData).subscribe({
        next: () => {
          this.selectedDate = this.addBirthdayForm.get("selectDateForm")?.value;
          this.refreshData();
          this.showToast("Birthday updated successfully!");
          const collapseElement = this.orderExpand.nativeElement;
          setTimeout(() => {
            this.renderer.addClass(collapseElement, 'collapsing');
            this.renderer.removeClass(collapseElement, 'collapse');
            this.renderer.removeClass(collapseElement, 'show');
            this.toggleBackdrop(false);
          }, 350);
        },
        error: (error) => {
          // console.log("Error editing birthday", error);
          this.showToast('Error updating birthday', 'error');
        },
        complete: () => {
          this.loadingSpinnerService.hide();
        }
      });
    } else {
      // Use service for new birthdays
      const birthdayData: BirthdayData = {
        birthdayName: this.addBirthdayForm.get("name")?.value.trim(),
        birthdayYears: this.addBirthdayForm.get("age")?.value,
        numberOfMagnets: this.addBirthdayForm.get("numberOfKids")?.value,
        note: this.addBirthdayForm.get("orderNote")?.value,
        startTime: Timestamp.fromDate(new Date(startDateTime)),
        endTime: Timestamp.fromDate(new Date(endDateTime)),
        locationId: this.addBirthdayForm.get("location")?.value,
        roomId: this.addBirthdayForm.get("room")?.value,
        orderStatus: orderStatus,
        playgroundId: this.selectedPlaygroundId,
        createdDate: Timestamp.fromDate(new Date()),
        email: "email@email.com",
        number: "0800 123 123",
        services: [this.addBirthdayForm.get("choice")?.value === 'male' ? environment.serviceIds.MALE_ID : environment.serviceIds.FEMALE_ID],
        uid: '', // Will be set by Firebase
        orgId: this.businessId,
        savedLocallyByUser: false,
        deliveryAddress: '',
        organizationName: '',
        staffId: environment.staffId
      };

      this.ordersApi.createBirthday(birthdayData).subscribe({
        next: (docId) => {
          // Update the document with its own UID
          this.ordersApi.updateBirthday(docId, { uid: docId }).subscribe({
            next: () => {
              this.selectedDate = this.addBirthdayForm.get("selectDateForm")?.value;
              this.resetFormState();
              this.refreshData();
              this.showToast("Birthday created successfully!");
              const collapseElement = this.orderExpand.nativeElement;
              setTimeout(() => {
                this.renderer.addClass(collapseElement, 'collapsing');
                this.renderer.removeClass(collapseElement, 'collapse');
                this.renderer.removeClass(collapseElement, 'show');
                this.toggleBackdrop(false);
              }, 350);
            },
            error: (error) => {
              // console.log("Error updating birthday with UID", error);
              this.showToast('Error updating birthday with UID', 'error');
            }
          });
        },
        error: (error) => {
          // console.log("Error creating birthday", error);
          this.showToast('Error creating birthday', 'error');
        },
        complete: () => {
          this.loadingSpinnerService.hide();
        }
      });
    }
  }

  // generatePaymentLink() {
  //   const headers = new HttpHeaders({
  //     Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJkYXRhIjp7Il9pZCI6Imd1bGxpLXdlYmhvb2stdG9rZW4ifSwiaWF0IjoxNzU0MzY1Njg3LCJleHAiOjE3ODU5MDE2ODd9.szt7GrWO4iT1ZCHFuTVXyrg92kBylRgHn3MPlpbbTpE`,
  //     'Content-Type': 'application/json'
  //   });

  //   const body = {
  //     birthday_id: this.birthdayCard.id
  //   };

  //   return this.http.post(environment.paymentLinkUrl, body, { headers });
  // }

  // onGeneratePaymentLink() {
  //   if (!this.birthdayCard?.id || this.isGeneratingPaymentLink) return;
  //   this.isGeneratingPaymentLink = true;
  //   this.loadingSpinnerService.show();
  //   this.generatePaymentLink().subscribe({
  //     next: (res: any) => {
  //       const link: string = res?.data?.paymentLink || res?.paymentLink || res?.link || '';
  //       if (!link) {
  //         this.showToast('Failed to generate link', 'error');
  //         return;
  //       }
  //       const headers = new HttpHeaders({
  //         'Content-Type': 'application/json',
  //         'x-api-key': `${this.businessId}`
  //       });
  //       const body = { birthdayId: this.birthdayCard.id, paymentLink: link };
  //       this.http.post<any>('https://gulli-api.echo-dev.net/v1/updateBirthdayDetails', body, { headers }).subscribe({
  //         next: () => {
  //           this.birthdayCard.paymentLink = link;
  //           this.showToast('Payment link generated');
  //         },
  //         error: (err) => {
  //           console.error('Error saving payment link', err);
  //           this.showToast('Error saving payment link', 'error');
  //         },
  //         complete: () => {
  //           this.loadingSpinnerService.hide();
  //           this.isGeneratingPaymentLink = false;
  //         }
  //       });
  //     },
  //     error: (error) => {
  //       console.error('Error generating payment link', error);
  //       this.showToast('Error generating payment link', 'error');
  //       this.loadingSpinnerService.hide();
  //       this.isGeneratingPaymentLink = false;
  //     }
  //   });
  // }

  // copyPaymentLink(link: string) {
  //   if (!link) return;
  //   navigator.clipboard.writeText(link).then(() => {
  //     this.showToast('Copied');
  //   }).catch(() => {
  //     this.showToast('Copy failed', 'error');
  //   });
  // }

  private orderStatus() {

    if (this.isFunCityPlayground && !this.editMode) {
      return 'waiting_confirmation';
    }

    const now = new Date().getTime();
    const selectedDateValue = this.addBirthdayForm.get('selectDateForm')?.value;
    const selectedDate = new Date(selectedDateValue).getTime();
    // Calculate the difference in days
    const differenceInDays = Math.ceil((selectedDate - now) / (1000 * 60 * 60 * 24)); // Convert ms to days
    const orderStatus = differenceInDays > this.deliveryDays ? 'pending' : 'generic_magnet';
    // If in edit mode and the birthday is reserved, use the form value
    if (this.editMode && this.birthdayCard?.orderStatus === 'waiting_confirmation') {
      const formOrderStatus = this.addBirthdayForm.get('orderStatus')?.value;
      if (formOrderStatus === 'confirmed') {
        return orderStatus;
      }
      return 'waiting_confirmation';
    }
    return orderStatus;
  }

  private showToast(message: string, type: 'success' | 'error' = 'success') {
    const el = this.birthdayToast.nativeElement as HTMLElement;
    el.textContent = message;
    el.classList.remove('text-bg-success', 'text-bg-danger');
    el.classList.add(type === 'success' ? 'text-bg-success' : 'text-bg-danger');
    el.classList.add('show');
    setTimeout(() => {
      el.classList.remove('show');
    }, 3000);
  }

  expandFormWithCardData(card: any) {
    // console.log(card);
    const now = new Date().getTime();
    const startTime = new Date(card.startTime).getTime();
    const canEdit = (
      (card.orderStatus === "generic_magnet" && startTime > now) ||
      card.orderStatus === "pending" || (card.orderStatus === "waiting_confirmation" && startTime > now)
    );
    this.isReadOnly = !canEdit;
    this.editMode = true;
    this.birthdayCard = card;
    this.selectedValue = card.services[0] === environment.serviceIds.MALE_ID ? 'male' : 'female';
    // Use local date for form input value
    const date = this.dateFormatter.formatLocalDateForInput(new Date(card.startTime));
    // Check if the birthday is reserved and show status dropdown
    this.isReserved = card.orderStatus === 'waiting_confirmation';
    // Get locations for this playground
    this.getLocationsForPlayground(card.playgroundId);

    this.addBirthdayForm.patchValue({
      choice: this.selectedValue,
      name: card.birthdayName,
      age: card.birthdayYears,
      selectDateForm: date,
      playground: card.playgroundId, // UI-only field
      location: card.locationId,
      room: card.roomId,
      numberOfKids: card.numberOfMagnets,
      startTime: this.dateFormatter.formatTime(card.startTime),
      endTime: this.dateFormatter.formatTime(card.endTime),
      orderNote: card.note,
      orderStatus: card.orderStatus,
    });

    if (this.managedPlaygrounds.length > 1) {
      const playgroundMeta = this.managedPlaygrounds.find(p => p.uid === card.playgroundId);
      if (playgroundMeta && playgroundMeta.businessId) {
        this.selectedPlaygroundId = playgroundMeta.businessId;
        // Update FunCity flag based on the resolved business id
        this.isFunCityPlayground = this.funCityPlaygroundsIds.includes(this.selectedPlaygroundId);
      }
    }

    if (this.isReadOnly) {
      if (card.cancellationMessage) {
        this.cancellationReason = card.cancellationMessage;
      }
      this.addBirthdayForm.disable({ emitEvent: false });
    } else {
      this.addBirthdayForm.enable({ emitEvent: false });
    }

    this.expandForm();
    // Handle the selection hierarchy after a delay to ensure locations are loaded
    setTimeout(() => {
      this.handleLocationChange(card.locationId);
      this.handleRoomChange(card.roomId);
    }, 100);
  }

  deleteCard() {
    this.loadingSpinnerService.show();

    this.ordersApi.deleteBirthday(this.birthdayCard.id).subscribe({
      next: () => {
        const collapseElement = this.orderExpand.nativeElement;
        setTimeout(() => {
          this.renderer.addClass(collapseElement, 'collapsing');
          this.renderer.removeClass(collapseElement, 'collapse');
          this.renderer.removeClass(collapseElement, 'show');
          const modalBackdrop = document.querySelector('.modal-backdrop.show');
          if (modalBackdrop) {
            modalBackdrop.classList.remove('show');
          }
          this.toggleBackdrop(false);
        }, 350);
        this.birthdayDelete.nativeElement.classList.remove('show');
        this.showToast("Birthday deleted successfully!");
        // Refresh data from database to ensure consistency
        this.refreshData();
      },
      error: (error) => {
        // console.log("Error deleting birthday", error);
        this.showToast('Error deleting birthday', 'error');
      },
      complete: () => {
        this.loadingSpinnerService.hide();
      }
    });
  }

  cancelBirthday(birthdayId: string) {
    const reason = this.cancelReason.trim();
    if (!reason) {
      this.showToast('Please provide a cancellation reason', 'error');
      return;
    }

    this.loadingSpinnerService.show();

    this.ordersApi.cancelBirthday(birthdayId, reason).subscribe({
      next: () => {
        const collapseElement = this.orderExpand.nativeElement;
        setTimeout(() => {
          this.renderer.addClass(collapseElement, 'collapsing');
          this.renderer.removeClass(collapseElement, 'collapse');
          this.renderer.removeClass(collapseElement, 'show');
          const modalBackdrop = document.querySelector('.modal-backdrop.show');
          if (modalBackdrop) {
            modalBackdrop.classList.remove('show');
          }
          this.toggleBackdrop(false);
        }, 350);
        this.cancelBirthdayModal.nativeElement.classList.remove('show');
        this.showToast("Birthday cancelled successfully!");
        // Refresh data from database to show updated status
        this.refreshData();
      },
      error: (error) => {
        // console.log("Error cancelling birthday", error);
        this.showToast('Error cancelling birthday', 'error');
      },
      complete: () => {
        this.loadingSpinnerService.hide();
      }
    });
  }

  expandForm() {
    const collapseElement = this.orderExpand.nativeElement;

    if (!collapseElement.classList.contains('show')) {
      this.renderer.addClass(collapseElement, 'collapsing');
      this.renderer.removeClass(collapseElement, 'collapse');

      setTimeout(() => {
        this.renderer.removeClass(collapseElement, 'collapsing');
        this.renderer.addClass(collapseElement, 'collapse');
        this.renderer.addClass(collapseElement, 'show');

        this.toggleBackdrop(true);
      }, 350);
    }
  }

  ngOnInit(): void {
    this.mpIds = JSON.parse(localStorage.getItem('mp') || '[]');

    // Initialize the service
    this.ordersApi.initialize(this.mpIds, this.businessId);

    // Listen for changes to 'selectDateForm' and react accordingly
    this.formControls['selectDateForm'].valueChanges.subscribe((selectedDate) => {
      if (selectedDate) {
        this.getReservedBirthdaysForm();
      }
    });

    const currentDate = new Date();
    this.today = this.dateFormatter.formatLocalDateForInput(currentDate);
    this.selectedDate = this.today;
    this.minDate = this.dateFormatter.formatLocalDateForInput(new Date());
    
    // Get managed playgrounds first
    this.getManagedPlaygrounds();
    // Get locations after playgrounds are loaded
    this.getLocations();

    // this.getDeliveryDays();
    this.funCityPlaygroundsIds.includes(this.businessId) ? this.isFunCityPlayground = true : false;

    // Load initial birthdays
    this.getBirthdays(this.selectedDate);
  }

  ngOnDestroy() {
    // Clean up flatpickr instances to prevent memory leaks
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
    }
    if (this.flatpickrInstance2) {
      this.flatpickrInstance2.destroy();
    }
  }

  // Location filter handlers
  toggleAllLocations(selected: boolean) {
    this.allLocationsSelected = selected;
    if (selected) {
      this.tempSelectedLocationIds = this.allLocations.map(l => l.id);
    } else {
      this.tempSelectedLocationIds = [];
    }
  }

  toggleLocationSelection(locationId: string, selected: boolean) {
    if (selected) {
      if (!this.tempSelectedLocationIds.includes(locationId)) {
        this.tempSelectedLocationIds = [...this.tempSelectedLocationIds, locationId];
      }
    } else {
      this.tempSelectedLocationIds = this.tempSelectedLocationIds.filter(id => id !== locationId);
    }
    // If any manually unselected, All should be deselected; if all selected, All selected
    this.allLocationsSelected = this.tempSelectedLocationIds.length === this.allLocations.length;
  }

  getFilteredLocations() {
    if (!this.locationSearchTerm || this.locationSearchTerm.trim() === '') {
      return this.allLocations || [];
    }
    const searchLower = this.locationSearchTerm.toLowerCase().trim();
    return (this.allLocations || []).filter(location => 
      location.name.toLowerCase().includes(searchLower)
    );
  }

  applyLocationFilter() {
    this.selectedLocationIds = [...this.tempSelectedLocationIds];
    this.allLocationsSelected = this.selectedLocationIds.length === this.allLocations.length;
    // Re-calculate magnets count based on filtered locations
    this.calculateMagnetsCount();
    // Re-group to apply filter
    this.groupData();
    // Close the dropdown
    this.closeLocationFilterDropdown();
  }

  private closeLocationFilterDropdown() {
    if (this.locationFilterDropdown?.nativeElement) {
      const dropdownElement = this.locationFilterDropdown.nativeElement;
      // Try Bootstrap 5 API first
      const bootstrap = (window as any).bootstrap;
      if (bootstrap && bootstrap.Dropdown) {
        const dropdownInstance = bootstrap.Dropdown.getInstance(dropdownElement);
        if (dropdownInstance) {
          dropdownInstance.hide();
          return;
        }
      }
      // Fallback: manually close the dropdown using DOM
      dropdownElement.classList.remove('show');
      dropdownElement.setAttribute('aria-expanded', 'false');
      const menu = dropdownElement.nextElementSibling as HTMLElement;
      if (menu) {
        menu.classList.remove('show');
      }
    }
  }

  toggleAllStatuses(selected: boolean) {
    this.allStatusesSelected = selected;
    if (selected) {
      // When All is selected, select all status options (excluding 'all')
      this.tempSelectedStatuses = this.statusOptions
        .filter(s => s.value !== 'all')
        .map(s => s.value);
    } else {
      this.tempSelectedStatuses = [];
    }
  }

  toggleStatusSelection(status: string, selected: boolean) {
    if (status === 'all') return;
    if (selected) {
      if (!this.tempSelectedStatuses.includes(status)) {
        this.tempSelectedStatuses = [...this.tempSelectedStatuses, status];
      }
    } else {
      this.tempSelectedStatuses = this.tempSelectedStatuses.filter(s => s !== status);
    }
    // If any manually unselected, All should be deselected; if all selected, All selected
    const allStatusValues = this.statusOptions.filter(s => s.value !== 'all').map(s => s.value);
    this.allStatusesSelected = this.tempSelectedStatuses.length === allStatusValues.length;
  }

  applyStatusFilter() {
    this.selectedStatuses = [...this.tempSelectedStatuses];
    this.allStatusesSelected = this.selectedStatuses.length === this.statusOptions.filter(s => s.value !== 'all').length;
    // Re-group data to apply filter
    this.groupData();
    // Close the dropdown
    this.closeStatusFilterDropdown();
  }

  private closeStatusFilterDropdown() {
    if (this.statusFilterDropdown?.nativeElement) {
      const dropdownElement = this.statusFilterDropdown.nativeElement;
      // Try Bootstrap 5 API first
      const bootstrap = (window as any).bootstrap;
      if (bootstrap && bootstrap.Dropdown) {
        const dropdownInstance = bootstrap.Dropdown.getInstance(dropdownElement);
        if (dropdownInstance) {
          dropdownInstance.hide();
          return;
        }
      }
      // Fallback: manually close the dropdown using DOM
      dropdownElement.classList.remove('show');
      dropdownElement.setAttribute('aria-expanded', 'false');
      const menu = dropdownElement.nextElementSibling as HTMLElement;
      if (menu) {
        menu.classList.remove('show');
      }
    }
  }

  getStatusFilterLabel(): string {
    if (this.allStatusesSelected || this.selectedStatuses.length === 0) {
      return 'All';
    }
    return `${this.selectedStatuses.length} selected`;
  }

  private matchesStatusFilter(birthday: any): boolean {
    if (this.allStatusesSelected || this.selectedStatuses.length === 0) {
      return true;
    }

    const orderStatus = birthday.orderStatus;
    const statusToOrderStatuses: { [key: string]: string[] } = {
      personalized: ['pending', 'in_progress', 'ready_to_ship', 'order_completed'],
      reserved: ['waiting_confirmation'],
      generic: ['generic_magnet'],
      cancelled: ['cancelled']
    };

    return this.selectedStatuses.some(s => statusToOrderStatuses[s]?.includes(orderStatus));
  }

  // Determine if there are any birthdays visible after applying current filters
  hasVisibleBirthdays(): boolean {
    if (!this.groupedData || this.groupedData.length === 0) return false;
    return this.groupedData.some((group: any) =>
      Array.isArray(group.locations) && group.locations.some((loc: any) =>
        Array.isArray(loc.birthdays) && loc.birthdays.length > 0
      )
    );
  }

  // Check if a specific date group has any visible birthdays after applying filters
  hasVisibleBirthdaysInDateGroup(dateGroup: any): boolean {
    if (!dateGroup || !Array.isArray(dateGroup.locations)) return false;
    return dateGroup.locations.some((loc: any) =>
      Array.isArray(loc.birthdays) && loc.birthdays.length > 0
    );
  }

  // Magnet counts
  totalCelebrantMagnets: number = 0;
  totalReturnGiftMagnets: number = 0;

  private getFilteredPlaygrounds(): Playground[] {
    // If locations are filtered, get playgrounds from filtered locations
    if (!this.allLocationsSelected && this.selectedLocationIds.length > 0) {
      const filteredPlaygroundIds = new Set(
        this.allLocations
          .filter(loc => this.selectedLocationIds.includes(loc.id))
          .map(loc => loc.playgroundId)
      );
      return this.managedPlaygrounds.filter(p => filteredPlaygroundIds.has(p.uid));
    }
    // Use all managed playgrounds
    return this.managedPlaygrounds;
  }

  calculateMagnetsCount(): void {
    const playgrounds = this.getFilteredPlaygrounds();

    this.totalCelebrantMagnets = playgrounds.reduce((sum, p) => 
      sum + Math.max(0, p['magnetsInStockCelebrant'] || 0), 0
    );

    this.totalReturnGiftMagnets = playgrounds.reduce((sum, p) => 
      sum + Math.max(0, p['magnetsInStockReturnGift'] || 0), 0
    );
  }

  hasMagnetsProperties(): boolean {
    if (!this.managedPlaygrounds?.length) {
      return false;
    }

    const playgrounds = this.getFilteredPlaygrounds();
    
    return playgrounds.some(p => 
      p['magnetsInStockCelebrant'] !== undefined || 
      p['magnetsInStockReturnGift'] !== undefined
    );
  }

  getMagnetsLabel(): string {
    const userRole = localStorage.getItem('userRole');
    
    // If user role is 'user', don't show label (only one playground)
    if (userRole === 'user' || this.managedPlaygrounds.length <= 1) {
      return '';
    }

    // Check if all locations are selected based on applied filter
    // If no filter has been applied yet (selectedLocationIds is empty), check allLocationsSelected
    if (this.selectedLocationIds.length === 0) {
      // No filter applied yet, check if all are selected in temp state
      return this.allLocationsSelected ? 'All: ' : 'Selected playgrounds: ';
    }
    
    // Filter has been applied, check if all locations are selected
    const allSelected = this.selectedLocationIds.length === this.allLocations?.length;
    
    // If all locations are selected, show "All: "
    if (allSelected) {
      return 'All: ';
    }

    // If specific playgrounds are selected, show "Selected playgrounds: "
    return 'Selected playgrounds: ';
  }

}







